'use client';
import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import HeroValdProd from '@src/components/Sections/HeroValdProd';

import Container from '@src/components/Shared/Container';
import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import RenderButtons from '@src/components/Shared/RenderButtons';
import NextImage from '@src/components/Shared/NextImage';
import SectionHeader from '@src/components/Shared/SectionHeader';
import SocialEmbeds from '@src/components/Shared/SocialEmbeds';
import Testimonials from '@src/components/Sections/Testimonials';
import FeaturedProducts from '@src/components/Sections/FeaturedProducts';
import FeaturedTiers from '@src/components/Sections/FeaturedTiers';
import CallToAction from '@src/components/Sections/CallToAction';

import usePreviewContent from '@src/hooks/usePreviewContent';

import constructURL from '@src/utils/constructURL';

import { BridgePageClientPropTypes } from './BridgeAthleticPage.types';

const BridgePage: React.FC<BridgePageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({ languageCode: page?.languageCode?.toLowerCase(), slug: `/products/bridgeathletic`, baseURL: '' }),
  );
  setContentType('site-page');

  return (
    <>
      <HeroValdProd
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        ctfData={page?.hero?.items?.[0]}
        className='mt-[4rem]! md:flex-col! lg:mb-[6.5rem]! lg:min-h-[36rem]! lg:flex-row! xl:min-h-[50rem]! 2xl:mt-[-2rem]! 2xl:min-h-[calc(58.3vw-26rem)]! min-[112.5rem]:min-h-[60rem]! [&_a]:font-[300]!'
        imgClassName='mt-[2rem] ml-[-30%] w-[calc(130%+2.4rem)] max-w-none self-start aspect-[3864/2316] [mask-image:linear-gradient(to_bottom,transparent,#000_15%)] md:mt-[-6rem] md:mr-[-6.3rem] md:ml-[0] md:w-[72rem] md:self-end md:[mask-image:linear-gradient(to_bottom,transparent,#000_15%,#000_85%,transparent),linear-gradient(to_right,transparent,#000_25%)] md:[mask-composite:intersect] lg:absolute lg:right-[calc((100%-100vw)/2)] lg:self-auto lg:top-[-4rem] lg:bottom-[0] lg:mt-[0] lg:mr-[0] lg:w-auto lg:[mask-image:none] 2xl:top-[0] min-[112.5rem]:right-auto min-[112.5rem]:left-[33.3rem] min-[112.5rem]:[mask-image:linear-gradient(to_right,#000_75%,transparent)]'
        logoClassName='mt-[3rem] md:mt-[5.7rem] max-w-[27.6386rem] mb-[2.4rem] md:max-w-[25.7rem] md:mt-[4.1rem] lg:max-w-[35.9302rem] 2xl:max-w-[53.96rem] xsm:min-h-[5.3219rem] md:min-h-[4.9484rem] lg:min-h-[6.9188rem] 2xl:min-h-[10.3906rem]'
        introClassName='[&>p]:tracking-[-0.0342rem] [&>p]:leading-[2.7rem] [&>p]:text-[1.8rem] [&>p]:max-w-[30.5rem] md:[&>p]:text-[2rem] md:[&>p]:leading-[3rem] md:[&>p]:tracking-[-0.036rem] md:[&>p]:max-w-[39.6rem] lg:[&>p]:max-w-[48.5rem] lg:[&>p]:text-[2.4rem] lg:[&>p]:leading-[3.6rem] 2xl:[&>p]:max-w-[58.3rem] [&_a]:text-[1.8rem] md:[&_a]:text-[2rem] lg:[&_a]:text-[2.4rem]'
        headerClassName='md:relative md:z-10 md:mx-[unset] md:w-[80%] md:self-start lg:self-center lg:w-[50%]'
      />

      <ImageTextSecondary
        id={page?.precisionProgrammingTools?.items?.[0]?.scrollTo?.slice(1)}
        className='overflow-visible! lg:mb-[0] lg:px-[4rem] lg:py-[8rem]'
        titleClassName='md:max-w-[45rem] md:locale-es:max-w-[55rem] md:locale-fr:max-w-[58rem]'
        pretitle={page?.precisionProgrammingTools?.items?.[0]?.preTitle}
        title={page?.precisionProgrammingTools?.items?.[0]?.title}
        imgURL={page?.precisionProgrammingTools?.items?.[0]?.mediaPrimary?.url}
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='2xl:max-w-[78.2rem]'
              bodyCopy={page?.precisionProgrammingTools?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.precisionProgrammingTools?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container
        id={page?.tailoredToYourLevel?.items?.[0]?.scrollTo?.slice(1)}
        className={`mt-[8rem] flex flex-col flex-col-reverse gap-[6.2rem] overflow-y-hidden md:mt-[4rem] lg:flex-row lg:items-center`}
      >
        <div className={`lg:relative lg:z-20 lg:ml-[40px] lg:w-[38%]`}>
          <SectionHeader
            className={`lg:relative lg:z-5 lg:py-[10rem]`}
            pretitle={page?.tailoredToYourLevel?.items?.[0]?.preTitle}
            title={page?.tailoredToYourLevel?.items?.[0]?.title}
            preTitleClassName='mb-[1.6rem]'
            titleClassName={`text-[2.8rem] leading-[3.2rem] mb-[1.8rem] md:max-w-[45rem] md:locale-fr:max-w-[57rem] lg:mb-[3.2rem] lg:text-[4.8rem] lg:leading-[5rem] lg:tracking-[-0.096rem]`}
            childrenClassName='relative'
            align='left'
          >
            <RenderBodyCopy bodyCopy={page?.tailoredToYourLevel?.items?.[0]?.bodyCopy?.json?.content} />
            <RenderButtons
              className='justify-left mt-[4rem]'
              items={page?.tailoredToYourLevel?.items?.[0]?.buttonCollection?.items}
            />
          </SectionHeader>
        </div>

        <div className='overflow-hidden rounded-4xl shadow-[0_50px_50px_-30px_rgba(0,0,0,0.15)] lg:relative lg:z-20 lg:w-[45%]'>
          <SocialEmbeds
            className='bg-cover [&_iframe]:rounded-4xl'
            data={page?.tailoredToYourLevel?.items?.[0]?.extraAssetsCollection?.items?.[0]}
          />
        </div>
      </Container>

      <NextImage
        id={page?.extensiveExerciseLibrary?.items?.[0]?.scrollTo?.slice(1)}
        src={page?.extensiveExerciseLibrary?.items?.[0]?.mediaPrimary?.url}
        alt='A portrait'
        className='mb-[4.5rem] h-[50rem] md:h-[45rem] lg:h-[72rem] 2xl:mb-[5.6rem] [&>div]:flex [&>div]:items-center [&>div]:justify-center [&>img]:object-top'
        imgClassName='grayscale opacity-60'
      >
        <Container className='mt-[8rem] flex max-h-[50rem] w-[90%] max-w-[82rem] flex-col items-center justify-center rounded-4xl bg-[#FFFFFF] p-[2rem] opacity-[0.9] shadow-[0_4px_20px_0_rgba(0,0,0,0.25)] md:max-w-[85rem] md:p-[4rem] xl:w-[63%]'>
          <SectionHeader
            className='h-full'
            preTitleClassName='mb-[1.6rem] sm:text-center'
            pretitle={page?.extensiveExerciseLibrary?.items?.[0]?.preTitle}
            title={page?.extensiveExerciseLibrary?.items?.[0]?.title}
            titleClassName='text-center text-[2.2rem] text-left! w-full! mx-auto lg:max-w-[45rem] lg:locale-es:max-w-[52rem] lg:locale-fr:max-w-[64rem] leading-[2.6rem] tracking-[-0.0418] text-body-copy--dark font-light text-[1.8rem] sm:text-center! md:leading-[3rem] md:text-[2.8rem] lg:text-[4.8rem] lg:leading-[5rem]'
            childrenClassName='relative 2xl:max-w-[unset]!'
          >
            <RenderBodyCopy
              className='text-body-copy--dark md:locale-fr:max-w-[70rem] mt-[2.4rem] text-left font-extralight text-balance sm:text-center md:mx-auto md:max-w-[60rem] 2xl:mt-[3.2rem]'
              bodyCopy={page?.extensiveExerciseLibrary?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons
              className='justify-center'
              items={page?.extensiveExerciseLibrary?.items?.[0]?.buttonCollection?.items}
            />
          </SectionHeader>
        </Container>
      </NextImage>

      <ImageTextSecondary
        reversed
        className='lg:mt-[11.2rem] lg:py-[0]'
        background='lg:ml-[unset]! lg:mr-[unset]! lg:w-full! '
        id={page?.bridgeathleticMobileApp?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.bridgeathleticMobileApp?.items?.[0]?.preTitle}
        title={page?.bridgeathleticMobileApp?.items?.[0]?.title}
        imgURL={page?.bridgeathleticMobileApp?.items?.[0]?.mediaPrimary?.url}
        titleClassName='md:max-w-[41rem] md:locale-es:max-w-[63rem] md:locale-fr:max-w-[53rem]'
        imgAlt={page?.bridgeathleticMobileApp?.items?.[0]?.mediaPrimary?.description}
        objectFit='contain'
        imgSecondClassName='lg:w-[35%]!'
        childrenElements={
          <>
            <RenderBodyCopy
              className='max-w-[51.7rem] text-left 2xl:max-w-[78.2rem]'
              bodyCopy={page?.bridgeathleticMobileApp?.items?.[0]?.bodyCopy?.json?.content}
            />

            <RenderButtons
              className='w-full'
              items={page?.bridgeathleticMobileApp?.items?.[0]?.buttonCollection?.items}
            />
          </>
        }
      />

      <Container
        id={page?.importWithAI?.items?.[0]?.scrollTo?.slice(1)}
        className={`mt-[8rem] flex flex-col gap-[3.2rem] overflow-y-clip md:mt-[4rem] lg:flex-row lg:items-center`}
      >
        <div className='lg:relative lg:z-20 lg:w-[50%]'>
          <SocialEmbeds
            className='bg-cover [&_iframe]:rounded-4xl [&>div]:drop-shadow-[0_20px_20px_rgba(0,0,0,0.25)] [&>div>div]:bg-contain! lg:[&>div>div]:bg-left! lg:[&>div>div>svg]:left-[45%]!'
            data={page?.importWithAI?.items?.[0]?.extraAssetsCollection?.items?.[0]}
          />
        </div>

        <div className={`lg:relative lg:z-20 lg:ml-[40px] lg:w-[38%]`}>
          <SectionHeader
            className={`lg:relative lg:z-5 lg:py-[10rem]`}
            pretitle={page?.importWithAI?.items?.[0]?.preTitle}
            title={page?.importWithAI?.items?.[0]?.title}
            preTitleClassName='mb-[1.6rem]'
            titleClassName={`text-[2.8rem] leading-[3.2rem] mb-[1.8rem] md:max-w-[37.75rem] md:locale-fr:max-w-[54rem] md:locale-es:max-w-[40rem] lg:mb-[3.2rem] lg:text-[4.8rem] lg:leading-[5rem] lg:tracking-[-0.096rem]`}
            childrenClassName='relative'
            align='left'
          >
            <RenderBodyCopy bodyCopy={page?.importWithAI?.items?.[0]?.bodyCopy?.json?.content} />
            <RenderButtons
              className='justify-left mt-[4rem]'
              items={page?.importWithAI?.items?.[0]?.buttonCollection?.items}
            />
          </SectionHeader>
        </div>
      </Container>

      {/* <FeaturedTiers
        data={page?.bridgeathleticTiers?.items?.[0]}
        className='py-[4rem] lg:py-[8rem]'
        background='bg-[#F9F9F9]'
      /> */}

      <Testimonials
        id={page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={page?.testimonials?.items?.[0]?.topicCollection?.items}
        className='[&>div:last-child]:bg-[white]'
      />

      <FeaturedProducts
        data={page?.programmingResources?.items?.[0]}
        className='xl:[&>header>h2]:locale-fr:max-w-[77rem] py-[4rem] lg:py-[8rem] xl:[&>header>h2]:max-w-[70rem]'
        background='bg-[#F9F9F9]'
      />

      <FeaturedProducts
        data={page?.valdSystems?.items?.[0]}
        className='py-[4rem] lg:py-[8rem] xl:[&>header>h2]:max-w-[75rem]'
      />

      <CallToAction
        id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </>
  );
};

export default BridgePage;
